/**
 * Montants : toujours des entiers en centimes (HT sauf mention contraire), cf. CDC §0 et §3.1.
 */

/** Espace fine insécable (séparateur des milliers en français, comme Intl fr-FR). */
export const GROUP_SEPARATOR = String.fromCharCode(0x202f);
/** Espace insécable avant le symbole €. */
export const CURRENCY_SPACE = String.fromCharCode(0x00a0);

const SPACES_RE = new RegExp(`[\\s${GROUP_SEPARATOR}${CURRENCY_SPACE}]`, 'g');

export function isCents(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value);
}

export function assertCents(value: number): number {
  if (!isCents(value)) {
    throw new TypeError(`Montant en centimes invalide : ${String(value)}`);
  }
  return value;
}

/** Convertit des euros (nombre) en centimes entiers, arrondis au centime le plus proche. */
export function eurosToCents(euros: number): number {
  if (!Number.isFinite(euros)) throw new TypeError(`Montant en euros invalide : ${String(euros)}`);
  // toFixed(6) absorbe les erreurs binaires (1.005 * 100 = 100.49999…).
  return assertCents(Math.round(Number((euros * 100).toFixed(6))));
}

export function centsToEuros(cents: number): number {
  return assertCents(cents) / 100;
}

/**
 * Lit un montant saisi en français (« 12 345,67 € », « 1234.5 », « -80 ») et le renvoie en centimes.
 * Renvoie null si la saisie n'est pas un montant.
 */
export function parseEurosToCents(input: string): number | null {
  const cleaned = input.replace(SPACES_RE, '').replace(/€$/, '').replace(',', '.');
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) return null;
  const [, sign, whole = '0', fraction = ''] = match;
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  if (!Number.isSafeInteger(cents)) return null;
  return sign === '-' && cents !== 0 ? -cents : cents;
}

export function sumCents(values: readonly number[]): number {
  return assertCents(values.reduce((total, value) => total + assertCents(value), 0));
}

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
}

/**
 * Formate des centimes en euros à la française : `12 345 €` (arrondi à l'euro, par défaut)
 * ou `12 345,67 €` avec `{ cents: true }`. Même rendu que Intl.NumberFormat('fr-FR').
 */
export function formatEuros(cents: number, options: { cents?: boolean } = {}): string {
  assertCents(cents);
  const negative = cents < 0;
  const abs = Math.abs(cents);
  let body: string;
  let isZero: boolean;
  if (options.cents) {
    isZero = abs === 0;
    const whole = Math.floor(abs / 100);
    const fraction = String(abs % 100).padStart(2, '0');
    body = `${groupThousands(String(whole))},${fraction}`;
  } else {
    // Arrondi « au plus proche, moitié vers l'extérieur » sur la valeur absolue.
    const euros = Math.round(abs / 100);
    isZero = euros === 0;
    body = groupThousands(String(euros));
  }
  return `${negative && !isZero ? '-' : ''}${body}${CURRENCY_SPACE}€`;
}

/**
 * Lot Q (Maxime, 13/10/2026) : sous ce montant HT, un devis ou une facture vaut « 0 € » (arrondi à l'euro, comme
 * l'affichage `formatEuros`). L'agence fait des devis à 0 € pour faire signer un abonnement : ils ne sont ni des
 * « devis en attente », ni des relances, ni des factures à faire ; une facture à 0 € (ou dont le reste dû est
 * à 0 €) n'appelle jamais de rappel de paiement.
 */
export const ZERO_AMOUNT_BELOW_CENTS = 50;

/** Montant nul à l'euro près, ou négatif (avoir) : `cents < 50`. */
export function isZeroAmount(cents: number): boolean {
  return cents < ZERO_AMOUNT_BELOW_CENTS;
}
